import deepcopy from 'deepcopy';
import {
  ANDROID_DEVICE_FAMILY,
  APPLE_DEVICE_FAMILIES,
  APPLE_GROUP_DEVICE_FAMILIES,
  DEVICE_FAMILIES,
  DEVICE_LABEL_RUNS_ON_MAP,
  type DeviceFilter,
  type DeviceFilterHandlers,
  DeviceSelectValues,
  WINDOWS_DEVICE_FAMILY,
} from '../DeviceFamilyFilter.types';
import { DeviceFilterItem } from '../DeviceFilterItem';
import { findAndUpdateFilter } from './findAndUpdateFilter';

function toLabelArray(filters: DeviceFilter[]) {
  let labels = [];

  filters.forEach((filter) => {
    const { children } = filter;

    if (filter.value === DeviceSelectValues.CHECKED) {
      labels.push(filter.label);
    }

    if (children != null) {
      labels = [
        ...labels,
        ...children
          .filter((child) => child.value === DeviceSelectValues.CHECKED)
          .map((child) => child.label),
      ];
    }
  });

  return labels;
}

function fromIdArray(filterIds: string[], filters: DeviceFilter[]) {
  let newFilters = deepcopy(filters);

  filterIds.forEach((id) => {
    newFilters = findAndUpdateFilter(newFilters, id, {
      value: DeviceSelectValues.CHECKED,
    });
  });

  return newFilters;
}

// TODO: Handle deeply nested children.
function toDeviceFilterItems({
  filters,
  handlers,
  component: Component = DeviceFilterItem,
}: {
  filters: DeviceFilter[];
  handlers: DeviceFilterHandlers;
  component?: (props: any) => React.ReactNode;
}) {
  const { updateFilters } = handlers;

  return filters.map((filter) => {
    const { children } = filter;
    let items = [
      <Component
        key={filter.id}
        filter={filter}
        updateFilters={updateFilters}
        filters={filters}
        data-item-level="1"
      />,
    ];

    if (children != null) {
      items = [
        ...items,
        ...children.map((child) => (
          <Component
            key={child.id}
            asChild
            filter={child}
            updateFilters={updateFilters}
            filters={filters}
            data-item-level="2"
          />
        )),
      ];
    }

    return items;
  });
}

function toRunsOn(keys: string[]): string[] {
  return keys.reduce((acc, key) => {
    if (DEVICE_LABEL_RUNS_ON_MAP(key) == null) return acc;

    return [...acc, DEVICE_LABEL_RUNS_ON_MAP(key)];
  }, []);
}

/**
 * Returns the device families that match the given keys.
 * @param keys - ex: ["mac", "iphone", "windows", "appletv"]
 */
function deviceFamiliesFromIds(ids: Array<string>): DeviceFilter[] {
  const deviceFamilies = [WINDOWS_DEVICE_FAMILY(), ANDROID_DEVICE_FAMILY()];
  const hasCrossPlatformId = ids.some(
    (key) => key === 'windows' || key === 'android',
  );

  if (hasCrossPlatformId) {
    deviceFamilies.unshift(APPLE_GROUP_DEVICE_FAMILIES());
  } else {
    deviceFamilies.unshift(...APPLE_DEVICE_FAMILIES());
  }

  return deviceFamilies
    .map(({ children, ...device }) => {
      if (!children && ids.includes(device.id)) return { ...device, children };

      const filteredChildren = children?.filter(({ id }) => ids.includes(id));
      return filteredChildren?.length
        ? { ...device, children: filteredChildren }
        : null;
    })
    .filter(Boolean);
}

export const DeviceFilterTransformers = {
  toRunsOn,
  toLabelArray,
  fromIdArray,
  toDeviceFilterItems,
  deviceFamiliesFromIds,
};
